import { useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import { useRehabStates } from 'context/rehabStates';
import { useTranslation } from 'context/translations';
import { simpleDashDate } from 'lib/date';
import { RehabStateApiName } from 'models/RehabState';
import { createLocationEpisodeAdmission } from 'services/api/locationEpisodeAdmissions';
import { createLocationEpisodeRehabState } from 'services/api/locationEpisodeRehabStates';

const FormContainer = styled.div`
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
`;

export type AdmittedAtModalType = {
  setShow: (b: boolean) => void;
  invalidateData: () => void;
  locationEpisodeId: string;
  patientName: string;
  title?: string;
};

export default function AdmittedAtModal(props: AdmittedAtModalType) {
  const { setShow, locationEpisodeId, patientName, invalidateData, title } = props;
  const { profile } = useProfile();
  const useAdmissionEndpoint = profile?.hasFlag(Flags.SharedRecords);

  const { mutate: mutateRehabState, isPending: isRehabStatePending } = useMutation({
    mutationFn: createLocationEpisodeRehabState,
  });

  const { mutate: mutateAdmission, isPending: isAdmissionPending } = useMutation({
    mutationFn: createLocationEpisodeAdmission,
  });

  const isPending = isRehabStatePending || isAdmissionPending;

  const rehabStates = useRehabStates();
  const { translate } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState('');
  const formattedStartDate = simpleDashDate(startDate);

  const cancel = () => {
    setShow(false);
    setStartDate(null);
    setDateError('');
  };

  const onSuccess = () => {
    invalidateData();
    cancel();
  };

  const submit = () => {
    if (!formattedStartDate) return;

    if (useAdmissionEndpoint) {
      mutateAdmission({ locationEpisodeId, enteredAt: formattedStartDate }, { onSuccess });
    } else {
      // TODO: OLIO-2027 - Remove this branch once all admissions use the admission endpoint.
      // The rehabState endpoint is the legacy path for a simple admission..
      mutateRehabState(
        {
          locationEpisodeId,
          enteredAt: formattedStartDate,
          rehabStateId: rehabStates!.find((x) => x.apiName === RehabStateApiName.InTreatment)!.id,
        },
        { onSuccess }
      );
    }
  };

  return (
    <StandardModal title={title ?? `Confirm ${translate('Admission')} Date`} onCancel={cancel}>
      <PatientName>{patientName}</PatientName>
      <FormContainer>
        <InputGroup title={`Actual ${translate('Admission')} Date`} error={dateError} $containerWidth='auto'>
          <DatePicker
            $hasError={!!dateError}
            maxDate={new Date()}
            selected={startDate}
            onChange={(z) => {
              setDateError(z ? '' : 'This field is required');
              setStartDate(z);
            }}
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!formattedStartDate} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}
